import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Policy from '../login/Policy';
import meya_logo from '../../assets/images/logo_meya.png';

function JobAuthModal({ initialView = 'login', app_hostname = '', onClose, onAuthSuccess }) {
  // Modal View States
  const [currentAuthView, setCurrentAuthView] = useState(initialView || 'login');
  const [loginMethod, setLoginMethod] = useState('otp'); // 'otp' or 'password'
  const [authError, setAuthError] = useState('');
  const [alertMsg, setAlertMsg] = useState('');

  // Login Form States
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginPhone, setLoginPhone] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [loginOtpSent, setLoginOtpSent] = useState(false);
  const [loginOtpCountdown, setLoginOtpCountdown] = useState(0);
  const [jobRememberMe, setJobRememberMe] = useState(true);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Register Form States
  const [regNameInput, setRegNameInput] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  const [regOtp, setRegOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [isPolicyAccepted, setIsPolicyAccepted] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);

  // Synchronize initialView when changed from URL/parent
  useEffect(() => {
    if (initialView) {
      setCurrentAuthView(initialView);
      setAuthError('');
    }
  }, [initialView]);

  // Timers
  useEffect(() => {
    if (otpCountdown <= 0) return undefined;
    const timer = setTimeout(() => setOtpCountdown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpCountdown]);

  useEffect(() => {
    if (loginOtpCountdown <= 0) return undefined;
    const timer = setTimeout(() => setLoginOtpCountdown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [loginOtpCountdown]);

  // Alert toast trigger
  const triggerAlert = (msg) => {
    setAlertMsg(msg);
    setTimeout(() => {
      setAlertMsg('');
    }, 4000);
  };

  // Password validation rules (Meya-standard: 8+ chars, upper, lower, number)
  const passwordCriteria = {
    length: regPassword.length >= 8,
    hasUpper: /[A-Z]/.test(regPassword),
    hasLower: /[a-z]/.test(regPassword),
    hasNumber: /\d/.test(regPassword),
  };
  const isPasswordValid = Object.values(passwordCriteria).every(Boolean);
  const isPasswordMatch = regPassword.length > 0 && regPassword === regConfirmPassword;

  // Phone number format helper (08X-XXX-XXXX)
  const formatPhoneDigits = (digits) => {
    const d = (digits || '').replace(/\D/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  };

  // Convert raw input handling Thai Kedmanee keyboard, Thai digits, +66 prefix
  const parseRawPhone = (raw) => {
    if (!raw) return '';
    const thaiDigits = {
      '๐': '0',
      '๑': '1',
      '๒': '2',
      '๓': '3',
      '๔': '4',
      '๕': '5',
      '๖': '6',
      '๗': '7',
      '๘': '8',
      '๙': '9',
    };
    const kedmanee = {
      จ: '0',
      ๅ: '1',
      '/': '2',
      ภ: '4',
      ถ: '5',
      'ุ': '6',
      'ึ': '7',
      ค: '8',
      ต: '9',
    };
    const hasKedmanee = Array.from(raw).some((ch) => Boolean(kedmanee[ch]));

    let res = '';
    for (let i = 0; i < raw.length; i++) {
      const c = raw[i];
      if (thaiDigits[c] !== undefined) {
        res += thaiDigits[c];
      } else if (kedmanee[c] !== undefined) {
        res += kedmanee[c];
      } else if (c === '-' && hasKedmanee) {
        res += '3';
      } else if (/[0-9]/.test(c)) {
        res += c;
      }
    }
    if (res.startsWith('66') && res.length > 10) {
      res = '0' + res.slice(2);
    }
    return formatPhoneDigits(res);
  };

  const handlePhoneKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (
      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(e.key)
    ) {
      if (e.key === 'Backspace') {
        const input = e.target;
        if (
          input &&
          input.selectionStart === input.selectionEnd &&
          input.selectionStart === input.value.length
        ) {
          if (input.value.endsWith('-')) {
            e.preventDefault();
            const cur = regPhone.replace(/\D/g, '');
            setRegPhone(formatPhoneDigits(cur.slice(0, -1)));
          }
        }
      }
      return;
    }
    const match = e.code && e.code.match(/^(?:Digit|Numpad)([0-9])$/);
    if (match) {
      e.preventDefault();
      const digit = match[1];
      const cur = regPhone.replace(/\D/g, '');
      if (cur.length < 10) {
        setRegPhone(formatPhoneDigits(cur + digit));
      }
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = parseRawPhone(e.target.value || '');
    setRegPhone(formatted);
  };

  const handleOtpKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (
      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(e.key)
    )
      return;
    const match = e.code && e.code.match(/^(?:Digit|Numpad)([0-9])$/);
    if (match) {
      e.preventDefault();
      const digit = match[1];
      if (regOtp.length < 6) {
        setRegOtp(regOtp + digit);
      }
    }
  };

  const handleOtpChange = (e) => {
    const raw = e.target.value || '';
    const thaiDigits = {
      '๐': '0',
      '๑': '1',
      '๒': '2',
      '๓': '3',
      '๔': '4',
      '๕': '5',
      '๖': '6',
      '๗': '7',
      '๘': '8',
      '๙': '9',
    };
    const kedmanee = {
      จ: '0',
      ๅ: '1',
      '/': '2',
      ภ: '4',
      ถ: '5',
      'ุ': '6',
      'ึ': '7',
      ค: '8',
      ต: '9',
    };
    const hasKedmanee = Array.from(raw).some((ch) => Boolean(kedmanee[ch]));
    let res = '';
    for (let i = 0; i < raw.length; i++) {
      const c = raw[i];
      if (thaiDigits[c] !== undefined) {
        res += thaiDigits[c];
      } else if (kedmanee[c] !== undefined) {
        res += kedmanee[c];
      } else if (c === '-' && hasKedmanee) {
        res += '3';
      } else if (/[0-9]/.test(c)) {
        res += c;
      }
    }
    setRegOtp(res.slice(0, 6));
  };

  const handleLoginPhoneKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (
      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(e.key)
    ) {
      if (e.key === 'Backspace') {
        const input = e.target;
        if (
          input &&
          input.selectionStart === input.selectionEnd &&
          input.selectionStart === input.value.length
        ) {
          if (input.value.endsWith('-')) {
            e.preventDefault();
            const cur = loginPhone.replace(/\D/g, '');
            setLoginPhone(formatPhoneDigits(cur.slice(0, -1)));
          }
        }
      }
      return;
    }
    const match = e.code && e.code.match(/^(?:Digit|Numpad)([0-9])$/);
    if (match) {
      e.preventDefault();
      const digit = match[1];
      const cur = loginPhone.replace(/\D/g, '');
      if (cur.length < 10) {
        setLoginPhone(formatPhoneDigits(cur + digit));
      }
    }
  };

  const handleLoginPhoneChange = (e) => {
    const formatted = parseRawPhone(e.target.value || '');
    setLoginPhone(formatted);
  };

  const handleLoginOtpKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (
      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(e.key)
    )
      return;
    const match = e.code && e.code.match(/^(?:Digit|Numpad)([0-9])$/);
    if (match) {
      e.preventDefault();
      const digit = match[1];
      if (loginOtp.length < 6) {
        setLoginOtp(loginOtp + digit);
      }
    }
  };

  const handleLoginOtpChange = (e) => {
    const raw = e.target.value || '';
    const thaiDigits = {
      '๐': '0',
      '๑': '1',
      '๒': '2',
      '๓': '3',
      '๔': '4',
      '๕': '5',
      '๖': '6',
      '๗': '7',
      '๘': '8',
      '๙': '9',
    };
    const kedmanee = {
      จ: '0',
      ๅ: '1',
      '/': '2',
      ภ: '4',
      ถ: '5',
      'ุ': '6',
      'ึ': '7',
      ค: '8',
      ต: '9',
    };
    const hasKedmanee = Array.from(raw).some((ch) => Boolean(kedmanee[ch]));
    let res = '';
    for (let i = 0; i < raw.length; i++) {
      const c = raw[i];
      if (thaiDigits[c] !== undefined) {
        res += thaiDigits[c];
      } else if (kedmanee[c] !== undefined) {
        res += kedmanee[c];
      } else if (c === '-' && hasKedmanee) {
        res += '3';
      } else if (/[0-9]/.test(c)) {
        res += c;
      }
    }
    setLoginOtp(res.slice(0, 6));
  };

  const handleForgotPassword = () => {
    setLoginMethod('otp');
    const digits = loginEmail.replace(/\D/g, '');
    if (digits.length >= 9 && digits.length <= 10) {
      setLoginPhone(formatPhoneDigits(digits));
    }
    setAuthError('');
  };

  // Auth Completion Handler (Sets sessions and dispatches onAuthSuccess)
  const completeAuth = ({ token, userId, username, email, alertMsg: customMsg }) => {
    if (token) {
      sessionStorage.setItem('job_token', token);
    }
    if (userId) {
      sessionStorage.setItem('job_user_id', String(userId));
    }
    sessionStorage.setItem('meya-job-loggedin', 'true');
    if (username) {
      sessionStorage.setItem('meya-job-username', username);
    }
    if (email) {
      sessionStorage.setItem('job_user_email', email);
    }
    setAuthError('');
    if (customMsg) {
      triggerAlert(customMsg);
    }
    if (onAuthSuccess) {
      onAuthSuccess({ token, userId, username, email, alertMsg: customMsg });
    }
  };

  // API Submission Handlers
  const handleSendLoginOtp = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const phone = loginPhone.replace(/\D/g, '');
    if (phone.length < 9 || phone.length > 10) {
      setAuthError('กรุณากรอกเบอร์โทรศัพท์ 9-10 หลักก่อนส่ง OTP');
      return;
    }
    if (loginOtpCountdown > 0) return;
    setAuthError('');
    setLoginOtpSent(true);
    setLoginOtpCountdown(60);

    try {
      const res = await axios.post((app_hostname || '') + '/api/account/job/auth/otp_send', {
        phone,
      });
      if (res.data && res.data.status === 'Success') {
        const debugOtp = res.data.data?.debug_otp;
        if (debugOtp) {
          console.log('[Jobs Auth] Login Debug OTP:', debugOtp);
        }
      } else {
        setAuthError(res.data?.message || 'ส่งรหัส OTP ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
      }
    } catch (err) {
      setAuthError('ระบบส่ง OTP ขัดข้อง กรุณาลองใหม่อีกครั้ง');
    }
  };

  const handleLoginWithOtp = async (e) => {
    e.preventDefault();
    const phone = loginPhone.replace(/\D/g, '');
    if (phone.length < 9 || phone.length > 10) {
      setAuthError('กรุณากรอกเบอร์โทรศัพท์ 9-10 หลัก');
      return;
    }
    if (!loginOtpSent) {
      setAuthError('กรุณากดส่งรหัส OTP เพื่อรับรหัสยืนยันเบอร์โทรศัพท์ก่อน');
      return;
    }
    if (!loginOtp.trim() || loginOtp.trim().length < 6) {
      setAuthError('กรุณากรอกรหัส OTP 6 หลักที่ได้รับ');
      return;
    }

    try {
      const res = await axios.post((app_hostname || '') + '/api/account/job/auth/otp_verify', {
        phone,
        otp: loginOtp.trim(),
        role: 'job_seeker',
      });
      if (res.data && res.data.status === 'Success') {
        const user = res.data.data?.user || {};
        const token = res.data.data?.token || '';
        const userId = user.id || user.user_id || '';
        const uname = user.username || `ผู้ใช้งาน ${phone}`;
        completeAuth({
          token,
          userId,
          username: uname,
          email: user.email || '',
          alertMsg: `เข้าสู่ระบบสำเร็จ ยินดีต้อนรับคุณ ${uname}!`,
        });
      } else {
        setAuthError(res.data?.message || 'รหัส OTP ไม่ถูกต้องหรือหมดอายุ');
      }
    } catch (err) {
      setAuthError('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง');
    }
  };

  const handleMainLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword.trim()) {
      setAuthError('กรุณากรอกเบอร์โทร/อีเมล และรหัสผ่าน');
      return;
    }
    try {
      const res = await axios.post((app_hostname || '') + '/api/account/job/auth/signin', {
        username: loginEmail.trim(),
        password: loginPassword.trim(),
      });
      if (res.data && res.data.status === 'Success') {
        const user = res.data.data?.user || {};
        const token = res.data.data?.token || '';
        const userId = user.id || user.user_id || '';
        const uname = user.username || loginEmail.split('@')[0] || 'User';
        completeAuth({
          token,
          userId,
          username: uname,
          email: user.email || (loginEmail.includes('@') ? loginEmail.trim() : ''),
          alertMsg: 'เข้าสู่ระบบสำเร็จ ยินดีต้อนรับเข้าสู่ระบบหางาน!',
        });
      } else {
        setAuthError(res.data?.message || 'เบอร์โทร/อีเมล หรือรหัสผ่านไม่ถูกต้อง');
      }
    } catch (err) {
      setAuthError('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง');
    }
  };

  const handleSendOtp = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const phone = regPhone.replace(/\D/g, '');
    if (phone.length < 9 || phone.length > 10) {
      setAuthError('กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้องก่อนส่ง OTP');
      return;
    }
    if (otpCountdown > 0) return;
    setAuthError('');
    setOtpSent(true);
    setOtpCountdown(60);

    try {
      const res = await axios.post((app_hostname || '') + '/api/account/job/auth/otp_send', {
        phone,
      });
      if (res.data && res.data.status === 'Success') {
        const debugOtp = res.data.data?.debug_otp;
        if (debugOtp) {
          console.log('[Jobs Auth] Debug OTP:', debugOtp);
        }
      } else {
        setAuthError(res.data?.message || 'ส่งรหัส OTP ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
      }
    } catch (err) {
      setAuthError('ระบบส่ง OTP ขัดข้อง กรุณาลองใหม่อีกครั้ง');
    }
  };

  const handleMainRegister = async (e) => {
    e.preventDefault();
    if (!regNameInput.trim()) {
      setAuthError('กรุณากรอกชื่อ-นามสกุล');
      return;
    }
    if (!regEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail.trim())) {
      setAuthError('กรุณากรอกอีเมลให้ถูกต้อง');
      return;
    }
    const phone = regPhone.replace(/\D/g, '');
    if (phone.length < 9 || phone.length > 10) {
      setAuthError('กรุณากรอกเบอร์โทรศัพท์ 9-10 หลัก');
      return;
    }
    if (!otpSent) {
      setAuthError('กรุณากดส่งรหัส OTP เพื่อยืนยันเบอร์โทรศัพท์ก่อน');
      return;
    }
    if (!regOtp.trim()) {
      setAuthError('กรุณากรอกรหัส OTP 6 หลักที่ได้รับ');
      return;
    }
    if (!isPasswordValid) {
      setAuthError(
        'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร ประกอบด้วย ตัวพิมพ์ใหญ่ พิมพ์เล็ก และตัวเลข',
      );
      return;
    }
    if (regPassword !== regConfirmPassword) {
      setAuthError('รหัสผ่านและยืนยันรหัสผ่านไม่ตรงกัน');
      return;
    }
    if (!isPolicyAccepted) {
      setAuthError('กรุณากดยอมรับเงื่อนไขการใช้บริการก่อนสมัครสมาชิก');
      return;
    }

    try {
      const res = await axios.post((app_hostname || '') + '/api/account/job/auth/register', {
        name: regNameInput.trim(),
        email: regEmail.trim(),
        password: regPassword.trim(),
        phone,
        otp: regOtp.trim(),
        role: 'job_seeker',
      });
      if (res.data && res.data.status !== 'Success') {
        setAuthError(res.data.message || 'การสมัครสมาชิกไม่สำเร็จ');
        return;
      }
      const token = res.data?.data?.token || '';
      const userId = res.data?.data?.user?.id || res.data?.data?.user?.user_id || '';
      const username = regNameInput.trim();
      completeAuth({
        token,
        userId,
        username,
        email: regEmail.trim(),
        alertMsg: `สมัครสมาชิกสำเร็จ! ยินดีต้อนรับคุณ ${username}`,
      });
    } catch (err) {
      setAuthError('ไม่สามารถเชื่อมต่อระบบลงทะเบียนได้ กรุณาลองใหม่อีกครั้ง');
    }
  };

  const loginPhoneDigits = loginPhone.replace(/\D/g, '');
  const canRequestLoginOtp = loginPhoneDigits.length >= 9;
  const loginOtpBtnDisabled = loginOtpCountdown > 0 || !canRequestLoginOtp;
  const isLogin = currentAuthView === 'login';

  return (
    <div
      className='job-auth-modal-overlay'
      role='dialog'
      aria-modal='true'
      aria-labelledby='job-auth-title'
    >
      {alertMsg && (
        <div className='job-alert-toast-floating job-auth-toast'>
          <i className='mdi mdi-check-circle-outline toast-icon'></i>
          <span>{alertMsg}</span>
        </div>
      )}

      <div className='job-auth-container'>
        <aside className='job-auth-aside' aria-hidden='true'>
          <div className='job-auth-aside-glow job-auth-aside-glow-a'></div>
          <div className='job-auth-aside-glow job-auth-aside-glow-b'></div>
          <div className='job-auth-aside-grid'></div>

          <div className='job-auth-aside-content'>
            <div className='job-auth-aside-brand'>
              <img src={meya_logo} alt='' className='job-auth-aside-logo' />
              <span>มียาหางาน</span>
            </div>

            <h2 className='job-auth-aside-title'>
              หางานสายสุขภาพ
              <br />
              ที่ใช่สำหรับคุณ
            </h2>
            <p className='job-auth-aside-desc'>
              รวมตำแหน่งงานจากร้านยา คลินิก และสถานพยาบาลทั่วประเทศ ในที่เดียว
            </p>

            <ul className='job-auth-aside-points'>
              <li>
                <i className='mdi mdi-flash-outline'></i>
                สมัครงานได้ทันทีด้วยโปรไฟล์เดียว
              </li>
              <li>
                <i className='mdi mdi-shield-check-outline'></i>
                ยืนยันตัวตนด้วย OTP ปลอดภัย
              </li>
              <li>
                <i className='mdi mdi-map-marker-radius-outline'></i>
                ค้นหาตำแหน่งใกล้คุณได้ง่าย
              </li>
            </ul>
          </div>
        </aside>

        <div className='job-auth-panel'>
          <button type='button' className='job-auth-close-btn' onClick={onClose} aria-label='ปิด'>
            <i className='mdi mdi-close'></i>
          </button>

          <div className='job-auth-panel-inner'>
            <header className='job-auth-header'>
              <p className='job-auth-kicker'>
                {isLogin ? 'ยินดีต้อนรับกลับ' : 'เริ่มต้นใช้งานฟรี'}
              </p>
              <h3 id='job-auth-title'>{isLogin ? 'เข้าสู่ระบบ' : 'สมัครสมาชิกผู้หางาน'}</h3>
              <p className='job-auth-subtitle'>
                {isLogin
                  ? 'เลือกวิธีเข้าสู่ระบบที่สะดวกสำหรับคุณ'
                  : 'กรอกข้อมูลสั้น ๆ เพื่อเริ่มหางานสายสุขภาพ'}
              </p>
            </header>

            {authError && (
              <div className='job-auth-error-alert' role='alert'>
                <i className='mdi mdi-alert-circle-outline'></i>
                <span>{authError}</span>
              </div>
            )}

            {isLogin ? (
              <div className='job-auth-form'>
                <div
                  className='job-login-method-switch'
                  role='tablist'
                  aria-label='วิธีเข้าสู่ระบบ'
                >
                  <button
                    type='button'
                    role='tab'
                    aria-selected={loginMethod === 'otp'}
                    className={`job-login-method-btn ${loginMethod === 'otp' ? 'active' : ''}`}
                    onClick={() => {
                      setLoginMethod('otp');
                      setAuthError('');
                    }}
                  >
                    เบอร์โทร OTP
                  </button>
                  <button
                    type='button'
                    role='tab'
                    aria-selected={loginMethod === 'password'}
                    className={`job-login-method-btn ${loginMethod === 'password' ? 'active' : ''}`}
                    onClick={() => {
                      setLoginMethod('password');
                      setAuthError('');
                    }}
                  >
                    อีเมล / รหัสผ่าน
                  </button>
                </div>

                {loginMethod === 'otp' ? (
                  <form onSubmit={handleLoginWithOtp} className='job-auth-form-inner'>
                    <div className='form-group'>
                      <label htmlFor='job-login-phone'>เบอร์โทรศัพท์</label>
                      <div className='job-phone-otp-row'>
                        <input
                          id='job-login-phone'
                          type='tel'
                          required
                          placeholder='08X-XXX-XXXX'
                          value={loginPhone}
                          onChange={handleLoginPhoneChange}
                          onKeyDown={handleLoginPhoneKeyDown}
                          autoComplete='tel'
                        />
                        <button
                          type='button'
                          className='job-otp-btn'
                          onClick={handleSendLoginOtp}
                          disabled={loginOtpBtnDisabled}
                        >
                          {loginOtpCountdown > 0
                            ? `ขออีกครั้ง (${loginOtpCountdown}s)`
                            : loginOtpSent
                              ? 'ส่งใหม่'
                              : 'ส่ง OTP'}
                        </button>
                      </div>
                    </div>

                    <div className='form-group'>
                      <label htmlFor='job-login-otp'>รหัส OTP (6 หลัก)</label>
                      <input
                        id='job-login-otp'
                        type='text'
                        maxLength={6}
                        required
                        placeholder='กรอกรหัส 6 หลัก'
                        value={loginOtp}
                        onChange={handleLoginOtpChange}
                        onKeyDown={handleLoginOtpKeyDown}
                        autoComplete='one-time-code'
                      />
                    </div>

                    <div className='job-auth-row-extra'>
                      <label className='job-remember-label'>
                        <input
                          type='checkbox'
                          checked={jobRememberMe}
                          onChange={(e) => setJobRememberMe(e.target.checked)}
                        />
                        <span>เข้าสู่ระบบค้างไว้</span>
                      </label>
                    </div>

                    <button type='submit' className='job-auth-btn-primary'>
                      เข้าสู่ระบบ
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleMainLogin} className='job-auth-form-inner'>
                    <div className='form-group'>
                      <label htmlFor='job-login-email'>เบอร์โทรศัพท์ หรือ อีเมล</label>
                      <input
                        id='job-login-email'
                        type='text'
                        required
                        placeholder='เช่น 0812345678 หรือ your@email.com'
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        autoComplete='username'
                      />
                    </div>

                    <div className='form-group'>
                      <label htmlFor='job-login-password'>รหัสผ่าน</label>
                      <div className='job-password-input-wrap'>
                        <input
                          id='job-login-password'
                          type={showLoginPassword ? 'text' : 'password'}
                          required
                          placeholder='กรอกรหัสผ่านของคุณ'
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          autoComplete='current-password'
                        />
                        <button
                          type='button'
                          className='job-password-eye-btn'
                          onClick={() => setShowLoginPassword((prev) => !prev)}
                          tabIndex={-1}
                          aria-label='แสดง/ซ่อนรหัสผ่าน'
                        >
                          <i
                            className={`mdi ${showLoginPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'}`}
                          ></i>
                        </button>
                      </div>
                    </div>

                    <div className='job-auth-row-extra'>
                      <label className='job-remember-label'>
                        <input
                          type='checkbox'
                          checked={jobRememberMe}
                          onChange={(e) => setJobRememberMe(e.target.checked)}
                        />
                        <span>เข้าสู่ระบบค้างไว้</span>
                      </label>
                      <button
                        type='button'
                        className='job-forgot-link-btn'
                        onClick={handleForgotPassword}
                      >
                        ลืมรหัสผ่าน? (เข้าด้วย OTP)
                      </button>
                    </div>

                    <button type='submit' className='job-auth-btn-primary'>
                      เข้าสู่ระบบ
                    </button>
                  </form>
                )}

                <p className='job-auth-switch-view'>
                  ยังไม่มีบัญชีผู้ใช้งาน?{' '}
                  <button
                    type='button'
                    className='job-auth-switch-link'
                    onClick={() => {
                      setCurrentAuthView('register');
                      setAuthError('');
                    }}
                  >
                    สมัครสมาชิกที่นี่
                  </button>
                </p>
              </div>
            ) : (
              <div className='job-auth-form'>
                <form onSubmit={handleMainRegister} className='job-auth-form-inner'>
                  <div className='form-group'>
                    <label htmlFor='job-reg-name'>ชื่อ - นามสกุล</label>
                    <input
                      id='job-reg-name'
                      type='text'
                      required
                      placeholder='เช่น ดลยา นวลดี'
                      value={regNameInput}
                      onChange={(e) => setRegNameInput(e.target.value)}
                      autoComplete='name'
                    />
                  </div>

                  <div className='form-group'>
                    <label htmlFor='job-reg-email'>อีเมล</label>
                    <input
                      id='job-reg-email'
                      type='email'
                      required
                      placeholder='เช่น your@email.com'
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      autoComplete='email'
                    />
                  </div>

                  <div className='form-group'>
                    <label htmlFor='job-reg-phone'>เบอร์โทรศัพท์ (ยืนยัน OTP)</label>
                    <div className='job-phone-otp-row'>
                      <input
                        id='job-reg-phone'
                        type='tel'
                        required
                        placeholder='08X-XXX-XXXX'
                        value={regPhone}
                        onChange={handlePhoneChange}
                        onKeyDown={handlePhoneKeyDown}
                        autoComplete='tel'
                      />
                      <button
                        type='button'
                        className='job-otp-btn'
                        onClick={handleSendOtp}
                        disabled={otpCountdown > 0 || regPhone.replace(/\D/g, '').length < 9}
                      >
                        {otpCountdown > 0
                          ? `ขออีกครั้ง (${otpCountdown}s)`
                          : otpSent
                            ? 'ส่งใหม่'
                            : 'ส่ง OTP'}
                      </button>
                    </div>
                  </div>

                  <div className='form-group'>
                    <label htmlFor='job-reg-otp'>รหัส OTP (6 หลัก)</label>
                    <input
                      id='job-reg-otp'
                      type='text'
                      maxLength={6}
                      required
                      placeholder='กรอกรหัส 6 หลักที่ได้รับทาง SMS'
                      value={regOtp}
                      onChange={handleOtpChange}
                      onKeyDown={handleOtpKeyDown}
                      autoComplete='one-time-code'
                    />
                    <small className='form-hint-text'>
                      กดปุ่ม &quot;ส่ง OTP&quot; ด้านบนเพื่อรับรหัสทาง SMS
                    </small>
                  </div>

                  <div className='job-auth-field-grid'>
                    <div className='form-group'>
                      <label htmlFor='job-reg-password'>ตั้งรหัสผ่าน</label>
                      <div className='job-password-input-wrap'>
                        <input
                          id='job-reg-password'
                          type={showRegPassword ? 'text' : 'password'}
                          required
                          placeholder='อย่างน้อย 8 ตัวอักษร'
                          value={regPassword}
                          onChange={(e) => setRegPassword(e.target.value)}
                          autoComplete='new-password'
                        />
                        <button
                          type='button'
                          className='job-password-eye-btn'
                          onClick={() => setShowRegPassword((prev) => !prev)}
                          tabIndex={-1}
                          aria-label='แสดง/ซ่อนรหัสผ่าน'
                        >
                          <i
                            className={`mdi ${showRegPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'}`}
                          ></i>
                        </button>
                      </div>
                    </div>

                    <div className='form-group'>
                      <label htmlFor='job-reg-confirm-password'>ยืนยันรหัสผ่าน</label>
                      <div className='job-password-input-wrap'>
                        <input
                          id='job-reg-confirm-password'
                          type={showRegConfirmPassword ? 'text' : 'password'}
                          required
                          placeholder='กรอกอีกครั้ง'
                          value={regConfirmPassword}
                          onChange={(e) => setRegConfirmPassword(e.target.value)}
                          autoComplete='new-password'
                        />
                        <button
                          type='button'
                          className='job-password-eye-btn'
                          onClick={() => setShowRegConfirmPassword((prev) => !prev)}
                          tabIndex={-1}
                          aria-label='แสดง/ซ่อนรหัสผ่านยืนยัน'
                        >
                          <i
                            className={`mdi ${showRegConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'}`}
                          ></i>
                        </button>
                      </div>
                      {regConfirmPassword.length > 0 && (
                        <small
                          className={`job-confirm-status ${isPasswordMatch ? 'match' : 'mismatch'}`}
                        >
                          <i
                            className={`mdi ${isPasswordMatch ? 'mdi-check-circle' : 'mdi-close-circle'}`}
                          ></i>
                          {isPasswordMatch ? 'รหัสผ่านตรงกัน' : 'รหัสผ่านไม่ตรงกัน'}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className='job-password-checklist job-password-checklist-inline'>
                    <span className={`checklist-chip ${passwordCriteria.length ? 'valid' : ''}`}>
                      8+ ตัวอักษร
                    </span>
                    <span className={`checklist-chip ${passwordCriteria.hasUpper ? 'valid' : ''}`}>
                      A-Z
                    </span>
                    <span className={`checklist-chip ${passwordCriteria.hasLower ? 'valid' : ''}`}>
                      a-z
                    </span>
                    <span className={`checklist-chip ${passwordCriteria.hasNumber ? 'valid' : ''}`}>
                      0-9
                    </span>
                  </div>

                  <div className='job-policy-check-group'>
                    <label className='job-policy-checkbox-label'>
                      <input
                        type='checkbox'
                        id='job-reg-agreeterm'
                        checked={isPolicyAccepted}
                        onChange={(e) => {
                          setIsPolicyAccepted(e.target.checked);
                          if (e.target.checked) setAuthError('');
                        }}
                      />
                      <span>
                        ฉันยอมรับ{' '}
                        <button
                          type='button'
                          className='job-policy-link-btn'
                          onClick={(e) => {
                            e.preventDefault();
                            setIsPolicyModalOpen(true);
                          }}
                        >
                          เงื่อนไขการใช้บริการ
                        </button>
                      </span>
                    </label>
                  </div>

                  <button type='submit' className='job-auth-btn-primary'>
                    สมัครสมาชิก
                  </button>
                </form>

                <p className='job-auth-switch-view'>
                  มีบัญชีแล้ว?{' '}
                  <button
                    type='button'
                    className='job-auth-switch-link'
                    onClick={() => {
                      setCurrentAuthView('login');
                      setAuthError('');
                    }}
                  >
                    เข้าสู่ระบบ
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {isPolicyModalOpen && (
        <div className='job-policy-modal-overlay' onClick={() => setIsPolicyModalOpen(false)}>
          <div className='job-policy-modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='job-policy-modal-header'>
              <h6>เงื่อนไขการใช้บริการ (Terms of Use)</h6>
              <button
                type='button'
                className='job-policy-modal-close'
                onClick={() => setIsPolicyModalOpen(false)}
              >
                <i className='mdi mdi-close'></i>
              </button>
            </div>
            <div className='job-policy-modal-body'>
              <Policy />
            </div>
            <div className='job-policy-modal-footer'>
              <button
                type='button'
                className='btn-policy-accept'
                onClick={() => {
                  setIsPolicyAccepted(true);
                  setIsPolicyModalOpen(false);
                  setAuthError('');
                }}
              >
                ยอมรับเงื่อนไข
              </button>
              <button
                type='button'
                className='btn-policy-decline'
                onClick={() => {
                  setIsPolicyAccepted(false);
                  setIsPolicyModalOpen(false);
                }}
              >
                ไม่ยอมรับ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

JobAuthModal.propTypes = {
  initialView: PropTypes.oneOf(['login', 'register']),
  app_hostname: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onAuthSuccess: PropTypes.func.isRequired,
};

JobAuthModal.defaultProps = {
  initialView: 'login',
  app_hostname: '',
};

export default JobAuthModal;
